import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT } from '../config';
import { ProjectileConfig } from '../types';
import { StickFigureGraphics } from '../rendering/StickFigureGraphics';

export class Projectile {
  scene: Phaser.Scene;
  x: number;
  y: number;
  damage: number;
  speed: number;
  piercing: boolean;
  projectileType: 'rock' | 'spear' | 'sling_stone' | 'crow' | 'rock_heavy' | 'beehive' | 'arrow' | 'firefly' | 'boomerang';
  graphics: Phaser.GameObjects.Graphics;
  isDestroyed: boolean;
  hitEnemyIds: Set<number>;
  angle: number;
  velocityX: number = 0;
  velocityY: number = 0;
  lobbing: boolean;
  lobTime: number;
  lobDuration: number;
  startX: number;
  startY: number;
  targetX: number;
  targetY: number;
  alpha: number = 1;

  // Slow effect properties
  slowEffect: number = 0;
  slowDuration: number = 0;
  aoeRadius: number = 0;
  target: any = null;
  isReturning: boolean = false;

  // Pierce limit (for spear upgrades)
  maxPierceCount: number = 0; // 0 = unlimited pierce

  // Poison effect
  poisonDamage: number = 0;
  poisonDuration: number = 0;

  // Fire effect
  fireDamage: number = 0;
  fireDuration: number = 0;

  // CC effects
  stunDuration: number = 0;
  knockbackDistance: number = 0;
  goldSteal: number = 5;

  private static nextId = 0;
  id: number;

  constructor(scene: Phaser.Scene, x: number, y: number, config: ProjectileConfig) {
    this.id = Projectile.nextId++;
    this.scene = scene;
    this.x = x;
    this.y = y;
    this.startX = x;
    this.startY = y;
    this.targetX = config.targetX;
    this.targetY = config.targetY;
    this.damage = config.damage;
    this.speed = config.speed;
    this.piercing = config.piercing;
    this.projectileType = config.projectileType;
    this.graphics = scene.add.graphics();
    this.isDestroyed = false;
    this.hitEnemyIds = new Set();
    this.lobbing = config.lobbing || false;
    this.lobTime = 0;
    this.alpha = config.alpha ?? 1;

    // Slow effect from sling stones
    this.slowEffect = config.slowEffect || 0;
    this.slowDuration = config.slowDuration || 0;
    this.aoeRadius = config.aoeRadius || 0;
    this.target = config.target || null;
    this.maxPierceCount = config.maxPierceCount || 0;
    this.poisonDamage = config.poisonDamage || 0;
    this.poisonDuration = config.poisonDuration || 0;
    this.fireDamage = config.fireDamage || 0;
    this.fireDuration = config.fireDuration || 0;
    this.stunDuration = config.stunDuration || 0;
    this.knockbackDistance = config.knockbackDistance || 0;
    this.goldSteal = config.goldSteal || 5;

    // Calculate trajectory
    const dx = config.targetX - x;
    const dy = config.targetY - y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    this.angle = Math.atan2(dy, dx);

    if (this.lobbing) {
      this.lobDuration = dist / this.speed * 1000;
    } else {
      this.lobDuration = 0;
      this.velocityX = (dx / dist) * this.speed;
      this.velocityY = (dy / dist) * this.speed;
    }
  }

  update(_time: number, delta: number): void {
    if (this.isDestroyed) return;

    if (this.projectileType === 'crow') {
      if (this.isReturning) {
        // Return to trainer
        const dx = this.startX - this.x;
        const dy = this.startY - this.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < 15) {
          this.isDestroyed = true;
          const goldAmount = this.goldSteal;
          const gameScene = this.scene as any;
          if (gameScene.economyManager) {
            gameScene.economyManager.addGold(goldAmount);
            if (gameScene.showFloatingText) {
              gameScene.showFloatingText(this.x, this.y, `+${goldAmount}g`, 0xffd700);
            }
          }
          this.destroy();
          return;
        }
        const angle = Math.atan2(dy, dx);
        this.angle = angle;
        this.velocityX = Math.cos(angle) * this.speed;
        this.velocityY = Math.sin(angle) * this.speed;
      } else {
        // Homing logic (attacking)
        if (this.target && !this.target.isDead && this.target.enemyType !== 'boulder') {
          this.targetX = this.target.x;
          this.targetY = this.target.y;
        } else {
          // Target dead/boulder/null — find new valid target
          this.target = null;
          if (this.scene && (this.scene as any).enemies) {
            const enemies = (this.scene as any).enemies;
            let nearest: any = null;
            let minDist = 9999;
            for (const e of enemies) {
              if (e.isDead) continue;
              if (e.enemyType === 'boulder') continue;
              if (e.isBurrowed || e.isGoblin) continue;
              const d = Math.sqrt((e.x - this.x) ** 2 + (e.y - this.y) ** 2);
              if (d < minDist) {
                minDist = d;
                nearest = e;
              }
            }
            if (nearest) {
              this.target = nearest;
              this.targetX = nearest.x;
              this.targetY = nearest.y;
            }
          }
        }
        // Re-calculate velocity towards target
        const dx = this.targetX - this.x;
        const dy = this.targetY - this.y;
        const angle = Math.atan2(dy, dx);
        this.angle = angle;
        this.velocityX = Math.cos(angle) * this.speed;
        this.velocityY = Math.sin(angle) * this.speed;
      }
    }

    if (this.projectileType === 'firefly') {
      // Homing: track living target, retarget if dead or boulder
      if (this.target && !this.target.isDead && this.target.enemyType !== 'boulder') {
        this.targetX = this.target.x;
        this.targetY = this.target.y;
      } else {
        this.target = null;
        if (this.scene && (this.scene as any).enemies) {
          const enemies = (this.scene as any).enemies;
          let nearest: any = null;
          let minDist = 9999;
          for (const e of enemies) {
            if (e.isDead) continue;
            if (e.enemyType === 'boulder') continue;
            if (e.isBurrowed || e.isGoblin) continue;
            const d = Math.sqrt((e.x - this.x) ** 2 + (e.y - this.y) ** 2);
            if (d < minDist) {
              minDist = d;
              nearest = e;
            }
          }
          if (nearest) {
            this.target = nearest;
            this.targetX = nearest.x;
            this.targetY = nearest.y;
          }
        }
      }
      // Recalculate velocity toward target
      const fdx = this.targetX - this.x;
      const fdy = this.targetY - this.y;
      const fAngle = Math.atan2(fdy, fdx);
      this.angle = fAngle;
      this.velocityX = Math.cos(fAngle) * this.speed;
      this.velocityY = Math.sin(fAngle) * this.speed;

      // Fly-like bobbing motion
      this.lobTime += delta;
      this.velocityY += Math.sin(this.lobTime * 0.008) * 15;
    }

    if (this.projectileType === 'boomerang') {
      if (!this.isReturning) {
        // Outward trip: travel toward targetX; flip when we reach it
        if (this.x >= this.targetX) {
          this.isReturning = true;
          this.hitEnemyIds.clear(); // Can hit same enemies on the way back
          // Reverse velocity
          this.velocityX = -Math.abs(this.velocityX);
          this.velocityY = -this.velocityY;
        }
      } else {
        // Return trip: head back to origin; destroy when we arrive
        if (this.x <= this.startX) {
          this.isDestroyed = true;
          this.destroy();
          return;
        }
      }
      // Spin the angle for visual
      this.angle += delta * 0.012;
    }

    if (this.lobbing) {
      // Parabolic arc
      this.lobTime += delta;
      const t = Math.min(this.lobTime / this.lobDuration, 1);
      this.x = this.startX + (this.targetX - this.startX) * t;
      this.y = this.startY + (this.targetY - this.startY) * t - Math.sin(t * Math.PI) * 80;

      if (t >= 1) {
        this.x = this.targetX;
        this.y = this.targetY;
      }
    } else {
      this.x += this.velocityX * (delta / 1000);
      this.y += this.velocityY * (delta / 1000);
    }

    // Out of bounds
    if (this.x > GAME_WIDTH + 50 || this.x < -50 || this.y > GAME_HEIGHT + 50 || this.y < -50) {
      this.isDestroyed = true;
      this.destroy();
      return;
    }

    // Lobbing projectile reached target
    if (this.lobbing && this.lobTime >= this.lobDuration) {
      // Will be handled by combat manager as area damage
    }

    this.draw();
  }

  draw(): void {
    this.graphics.clear();
    this.graphics.setAlpha(this.alpha);
    switch (this.projectileType) {
      case 'rock':
        StickFigureGraphics.drawRock(this.graphics, this.x, this.y);
        break;
      case 'spear':
        StickFigureGraphics.drawSpear(this.graphics, this.x, this.y, this.angle);
        break;

      case 'sling_stone':
        StickFigureGraphics.drawSlingStone(this.graphics, this.x, this.y);
        break;
      case 'crow':
        StickFigureGraphics.drawCrow(this.graphics, this.x, this.y);
        break;
      case 'rock_heavy':
        StickFigureGraphics.drawHeavyRock(this.graphics, this.x, this.y);
        break;
      case 'beehive':
        StickFigureGraphics.drawBeehive(this.graphics, this.x, this.y);
        break;
      case 'arrow':
        StickFigureGraphics.drawArrow(this.graphics, this.x, this.y, this.angle, this.hasPoisonEffect(), this.hasFireEffect());
        break;
      case 'firefly':
        StickFigureGraphics.drawFirefly(this.graphics, this.x, this.y);
        break;
      case 'boomerang':
        StickFigureGraphics.drawBoomerang(this.graphics, this.x, this.y, this.angle);
        break;
    }
  }

  destroy(): void {
    this.isDestroyed = true;
    this.graphics.destroy();
  }

  getBounds(): { x: number; y: number; radius: number } {
    return { x: this.x, y: this.y, radius: this.lobbing ? 25 : 10 };
  }

  hasSlowEffect(): boolean {
    return this.slowEffect > 0 && this.slowDuration > 0;
  }

  hasPoisonEffect(): boolean {
    return this.poisonDamage > 0 && this.poisonDuration > 0;
  }

  hasFireEffect(): boolean {
    return this.fireDamage > 0 && this.fireDuration > 0;
  }
}
